import React from 'react';
import { Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '@/modules/auth/ProtectedRoute';

// Lazy-loaded tab containers
const DirectorioPage = React.lazy(() => import('@/modules/hr/personal/DirectorioPage'));
const PuestosPage = React.lazy(() => import('@/modules/hr/puestos/PuestosPage'));
const AltasPermisosPage = React.lazy(() => import('@/modules/hr/altas-permisos/AltasPermisosPage'));
const NominaPage = React.lazy(() => import('@/modules/hr/nomina/NominaPage'));
const AdminNominaPage = React.lazy(() => import('@/modules/hr/admin-nomina/AdminNominaPage'));
const BienestarPage = React.lazy(() => import('@/modules/hr/bienestar/BienestarPage'));

// Detail routes (outside tabs)
const SubmissionDetailPage = React.lazy(() => import('@/modules/admin/submissions/SubmissionDetailPage'));
const UserDetailPage = React.lazy(() => import('@/modules/users/components/UserDetailPage'));
const DirectorioDetailPage = React.lazy(() => import('@/modules/hr/personal/DirectorioDetailPage'));

export const hrRoutes = [
  // Directorio y Expedientes (2 niveles + tabs)
  <Route key="/rrhh/personal" path="/rrhh/personal" element={
    <Navigate to="/rrhh/personal/directorio" replace />
  } />,
  <Route key="/rrhh/personal/directorio" path="/rrhh/personal/directorio" element={
    <ProtectedRoute requiredPermission="hr.management.view"><DirectorioPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/personal/expediente-digital" path="/rrhh/personal/expediente-digital" element={
    <Navigate to="/rrhh/personal/directorio" replace />
  } />,
  <Route key="/rrhh/personal/organigrama" path="/rrhh/personal/organigrama" element={
    <Navigate to="/rrhh/puestos/organigrama" replace />
  } />,
  // Detalle de persona del directorio (fuera del tab container)
  <Route key="/rrhh/personal/directorio/:id" path="/rrhh/personal/directorio/:id" element={
    <ProtectedRoute requiredPermission="hr.management.view"><DirectorioDetailPage /></ProtectedRoute>
  } />,

  // Puestos (2 niveles + tabs: organigrama, catálogo, raci, competencias, documentos)
  <Route key="/rrhh/puestos" path="/rrhh/puestos" element={
    <Navigate to="/rrhh/puestos/organigrama" replace />
  } />,
  <Route key="/rrhh/puestos/organigrama" path="/rrhh/puestos/organigrama" element={
    <ProtectedRoute requiredPermission="hr.management.view"><PuestosPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/puestos/catalogo" path="/rrhh/puestos/catalogo" element={
    <ProtectedRoute requiredPermission="hr.management.view"><PuestosPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/puestos/raci" path="/rrhh/puestos/raci" element={
    <ProtectedRoute requiredPermission="hr.management.view"><PuestosPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/puestos/competencias" path="/rrhh/puestos/competencias" element={
    <ProtectedRoute requiredPermission="hr.management.view"><PuestosPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/puestos/estructura-salarial" path="/rrhh/puestos/estructura-salarial" element={
    <ProtectedRoute requiredPermission="hr.salary.view"><PuestosPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/puestos/documentos" path="/rrhh/puestos/documentos" element={
    <ProtectedRoute requiredPermission="hr.management.view"><PuestosPage /></ProtectedRoute>
  } />,

  // Altas y Permisos (2 niveles + tabs)
  <Route key="/rrhh/altas-permisos" path="/rrhh/altas-permisos" element={
    <Navigate to="/rrhh/altas-permisos/contrataciones" replace />
  } />,
  <Route key="/rrhh/altas-permisos/contrataciones" path="/rrhh/altas-permisos/contrataciones" element={
    <ProtectedRoute requiredPermission="hr.hiring.view"><AltasPermisosPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/altas-permisos/alta-manual" path="/rrhh/altas-permisos/alta-manual" element={
    <ProtectedRoute requiredPermission="hr.hiring.view"><AltasPermisosPage /></ProtectedRoute>
  } />,
  // Legacy redirect: permisos movidos a /sistema/permisos
  <Route key="/rrhh/altas-permisos/permisos" path="/rrhh/altas-permisos/permisos" element={<Navigate to="/sistema/permisos/usuarios" replace />} />,
  // Detail route — submission detail vive fuera del tab container
  <Route key="/rrhh/altas-permisos/contrataciones/submissions/:id" path="/rrhh/altas-permisos/contrataciones/submissions/:id" element={
    <ProtectedRoute requiredPermission="hr.hiring.view"><SubmissionDetailPage /></ProtectedRoute>
  } />,

  // Nómina (2 niveles + tabs)
  <Route key="/rrhh/nomina" path="/rrhh/nomina" element={
    <Navigate to="/rrhh/nomina/docente" replace />
  } />,
  <Route key="/rrhh/nomina/docente" path="/rrhh/nomina/docente" element={
    <ProtectedRoute requiredPermission="hr.management.view"><NominaPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/nomina/administrativa" path="/rrhh/nomina/administrativa" element={
    <ProtectedRoute requiredPermission="hr.management.view"><NominaPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/nomina/consultorias" path="/rrhh/nomina/consultorias" element={
    <ProtectedRoute requiredPermission="hr.management.view"><NominaPage /></ProtectedRoute>
  } />,

  // Administración de Nómina (2 niveles + tabs)
  <Route key="/rrhh/admin-nomina" path="/rrhh/admin-nomina" element={
    <Navigate to="/rrhh/admin-nomina/timbrado-cfdi" replace />
  } />,
  <Route key="/rrhh/admin-nomina/timbrado-cfdi" path="/rrhh/admin-nomina/timbrado-cfdi" element={
    <ProtectedRoute requiredPermission="hr.management.view"><AdminNominaPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/admin-nomina/incidencias" path="/rrhh/admin-nomina/incidencias" element={
    <ProtectedRoute requiredPermission="hr.management.view"><AdminNominaPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/admin-nomina/beneficios" path="/rrhh/admin-nomina/beneficios" element={
    <ProtectedRoute requiredPermission="hr.management.view"><AdminNominaPage /></ProtectedRoute>
  } />,

  // Bienestar y Desarrollo (2 niveles + tabs) — FUSION de Desarrollo + Clima Laboral
  <Route key="/rrhh/bienestar" path="/rrhh/bienestar" element={
    <Navigate to="/rrhh/bienestar/capacitacion" replace />
  } />,
  <Route key="/rrhh/bienestar/capacitacion" path="/rrhh/bienestar/capacitacion" element={
    <ProtectedRoute requiredPermission="hr.management.view"><BienestarPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/bienestar/evaluacion-desempeno" path="/rrhh/bienestar/evaluacion-desempeno" element={
    <ProtectedRoute requiredPermission="hr.management.view"><BienestarPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/bienestar/etica" path="/rrhh/bienestar/etica" element={
    <ProtectedRoute requiredPermission="hr.management.view"><BienestarPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/bienestar/canal-denuncias" path="/rrhh/bienestar/canal-denuncias" element={
    <ProtectedRoute requiredPermission="hr.management.view"><BienestarPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/bienestar/encuesta-nom035" path="/rrhh/bienestar/encuesta-nom035" element={
    <ProtectedRoute requiredPermission="hr.management.view"><BienestarPage /></ProtectedRoute>
  } />,

  // User detail — ruta global, vive fuera de tabs
  <Route key="/usuarios/:id" path="/usuarios/:id" element={
    <ProtectedRoute requiredPermission="hr.users.view"><UserDetailPage /></ProtectedRoute>
  } />,

  // Legacy redirects from old paths
  <Route key="/rrhh/contrataciones" path="/rrhh/contrataciones" element={<Navigate to="/rrhh/altas-permisos/contrataciones" replace />} />,
  <Route key="/rrhh/contrataciones/submissions/:id" path="/rrhh/contrataciones/submissions/:id" element={
    <ProtectedRoute requiredPermission="hr.hiring.view"><SubmissionDetailPage /></ProtectedRoute>
  } />,
  <Route key="/rrhh/alta-manual" path="/rrhh/alta-manual" element={<Navigate to="/rrhh/altas-permisos/alta-manual" replace />} />,
  <Route key="/rrhh/permisos" path="/rrhh/permisos" element={<Navigate to="/sistema/permisos/usuarios" replace />} />,
  <Route key="/rrhh/nomina/timbrado-cfdi" path="/rrhh/nomina/timbrado-cfdi" element={<Navigate to="/rrhh/admin-nomina/timbrado-cfdi" replace />} />,
  <Route key="/rrhh/nomina/incidencias" path="/rrhh/nomina/incidencias" element={<Navigate to="/rrhh/admin-nomina/incidencias" replace />} />,
  <Route key="/rrhh/nomina/beneficios" path="/rrhh/nomina/beneficios" element={<Navigate to="/rrhh/admin-nomina/beneficios" replace />} />,
  <Route key="/rrhh/desarrollo/capacitacion" path="/rrhh/desarrollo/capacitacion" element={<Navigate to="/rrhh/bienestar/capacitacion" replace />} />,
  <Route key="/rrhh/desarrollo/evaluacion-desempeno" path="/rrhh/desarrollo/evaluacion-desempeno" element={<Navigate to="/rrhh/bienestar/evaluacion-desempeno" replace />} />,
  <Route key="/rrhh/clima-laboral/etica" path="/rrhh/clima-laboral/etica" element={<Navigate to="/rrhh/bienestar/etica" replace />} />,
  <Route key="/rrhh/clima-laboral/canal-denuncias" path="/rrhh/clima-laboral/canal-denuncias" element={<Navigate to="/rrhh/bienestar/canal-denuncias" replace />} />,
  <Route key="/rrhh/clima-laboral/encuesta-nom035" path="/rrhh/clima-laboral/encuesta-nom035" element={<Navigate to="/rrhh/bienestar/encuesta-nom035" replace />} />,
  <Route key="/admin/contrataciones" path="/admin/contrataciones" element={<Navigate to="/rrhh/altas-permisos/contrataciones" replace />} />,
  <Route key="/admin/submissions/:id" path="/admin/submissions/:id" element={
    <ProtectedRoute requiredPermission="hr.hiring.view"><SubmissionDetailPage /></ProtectedRoute>
  } />,
  <Route key="/admin/submissions" path="/admin/submissions" element={<Navigate to="/rrhh/altas-permisos/contrataciones?tab=submissions" replace />} />,
  <Route key="/admin/invitations" path="/admin/invitations" element={<Navigate to="/rrhh/altas-permisos/contrataciones?tab=invitations" replace />} />,
  <Route key="/registrations/manual" path="/registrations/manual" element={<Navigate to="/rrhh/altas-permisos/alta-manual" replace />} />,
  <Route key="/configuracion/usuarios" path="/configuracion/usuarios" element={<Navigate to="/sistema/permisos/usuarios" replace />} />,
  <Route key="/user-management/registration-templates" path="/user-management/registration-templates" element={<Navigate to="/sistema/plantillas/enrolamiento" replace />} />,
  <Route key="/rrhh/plantillas" path="/rrhh/plantillas" element={<Navigate to="/sistema/plantillas/enrolamiento" replace />} />,
];
